'use client';
import { MessageCircle, Clock, Send, CheckCircle, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { useT, useI18n } from '../../i18n';
import { sendTrack, getRef } from '../../lib/track';

const ContactSection = () => {
  const t = useT();
  const { locale } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'form' | 'verify' | 'success'>('form');
  const [verificationData, setVerificationData] = useState<{
    token: string;
    deepLink: string;
    message: string;
  } | null>(null);

  // ===========================================
  // CLOUDFLARE WORKER CONFIGURATION
  // ===========================================
  // Deployed Worker URL for JKD Coding WhatsApp Verification
  const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';
  // ===========================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit form data to Cloudflare Worker
      const response = await fetch(`${CF_WORKER_URL}/submit-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, locale, ref: getRef() }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      
      if (data.success) {
        sendTrack({ event: 'contact_submit' });
        setVerificationData({
          token: data.token,
          deepLink: data.deepLink,
          message: data.message,
        });
        setVerificationStep('verify');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(t('contact.alertFail'));
    }

    setIsSubmitting(false);
  };

  // Verification Step - Show WhatsApp Deep Link
  if (verificationStep === 'verify' && verificationData) {
    return (
      <section id="contact" className="py-12 lg:py-16 bg-jkd-gray-100/50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <div className="card-dark text-center">
              <div className="w-16 h-16 bg-jkd-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Smartphone className="w-8 h-8 text-jkd-gold" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-4">
                {t('contact.verify.title')}
              </h3>
              <p className="text-gray-400 mb-6">
                {t('contact.verify.desc')}
                <br /><br />
                Please click the button below to open WhatsApp and send the verification message to complete your submission.
              </p>

              {/* Token Display */}
              <div className="bg-jkd-black border border-jkd-gray-200 rounded-lg p-4 mb-6">
                <p className="text-gray-500 text-xs mb-2">{t('contact.verify.codeLabel')}</p>
                <p className="text-2xl font-mono font-bold text-jkd-gold tracking-wider">
                  {verificationData.token}
                </p>
              </div>

              {/* WhatsApp Deep Link Button */}
              <a
                href={verificationData.deepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-gold flex items-center justify-center gap-2 mb-4"
              >
                <MessageCircle className="w-5 h-5" />
                {t('contact.verify.openWa')}
              </a>

              <p className="text-gray-500 text-xs">
                {t('contact.verify.prefillNote')}
              </p>

              <div className="mt-6 pt-6 border-t border-jkd-gray-200">
                <button
                  onClick={() => setVerificationStep('form')}
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  {t('contact.verify.back')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Success Step
  if (verificationStep === 'success') {
    return (
      <section id="contact" className="py-12 lg:py-16 bg-jkd-gray-100/50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto text-center">
            <div className="card-dark">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t('contact.success.title')}
              </h3>
              <p className="text-gray-400">
                {t('contact.success.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Form Step (Default)
  return (
    <section id="contact" className="py-12 lg:py-16 bg-jkd-gray-100/50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-jkd-black text-jkd-gold text-sm font-mono tracking-widest mb-4 rounded">
            {t('contact.tag')}
          </span>
          <h2 className="section-title text-white">
            {t('contact.title')}
          </h2>
          <p className="text-jkd-gold text-lg mt-4 italic max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
          <div className="divider-gold mx-auto mt-6" />
        </div>

        {/* Contact Image */}
        <div className="max-w-sm mx-auto mb-8">
          <img
            src="/images/pubhjxqkwk.png"
            alt="Contact JKD Coding"
            className="w-full h-auto rounded-lg"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="card-dark">
            <h3 className="text-lg font-bold text-white mb-5">{t('contact.form.title')}</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1.5">{t('contact.form.name')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                    placeholder={t('contact.form.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1.5">{t('contact.form.email')}</label>
                  <input
                    type="email"
                    className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                    placeholder={t('contact.form.emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1.5">{t('contact.form.business')}</label>
                <select 
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.businessType}
                  onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                  required
                >
                  <option value="">{t('contact.form.businessPlaceholder')}</option>
                  <option value="ngo">{t('contact.form.businessNgo')}</option>
                  <option value="sme">{t('contact.form.businessSme')}</option>
                  <option value="personal">{t('contact.form.businessPersonal')}</option>
                  <option value="other">{t('contact.form.businessOther')}</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1.5">{t('contact.form.message')}</label>
                <textarea
                  rows={3}
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors resize-none"
                  placeholder={t('contact.form.messagePlaceholder')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>
              
              {/* Info Box */}
              <div className="bg-jkd-black/50 border border-jkd-gold/30 rounded-lg p-3">
                <p className="text-gray-400 text-xs flex items-start gap-2">
                  <Smartphone className="w-4 h-4 text-jkd-gold flex-shrink-0 mt-0.5" />
                  <span>
                    {t('contact.form.waNote')}
                    <br />
                    Next step requires WhatsApp verification. Please ensure you have access to WhatsApp.
                  </span>
                </p>
              </div>

              <button type="submit" className="w-full btn-gold flex items-center justify-center gap-2" disabled={isSubmitting}>
                {isSubmitting ? t('contact.form.processing') : t('contact.form.submit')}
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Quick Contact */}
            <div className="card-dark">
              <h3 className="text-lg font-bold text-white mb-5">{t('contact.quick.title')}</h3>
              {/* C-stage GEO upgrade: wa.me SSR-direct-output (rule 180 item 6) - href is written directly into raw HTML, click behavior is equivalent (opens in new tab) */}
              <a
                href={`https://wa.me/85251651480?text=${encodeURIComponent(t('contact.waPrefill'))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-4 p-4 bg-jkd-black rounded-lg hover:bg-jkd-gray-100 transition-colors group border border-transparent hover:border-jkd-gold/30"
              >
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium group-hover:text-jkd-gold transition-colors">
                    {t('contact.quick.whatsapp')}
                  </p>
                  <p className="text-gray-400 text-sm">{t('contact.quick.whatsappDesc')}</p>
                </div>
              </a>
            </div>

            {/* Response Time */}
            <div className="card-dark border-jkd-gold/30">
              <div className="flex items-center gap-4">
                <div className="text-jkd-gold">
                  <Clock className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-white font-medium">{t('contact.quick.response')}</p>
                  <p className="text-gray-400 text-sm">{t('contact.quick.responseDesc')}</p>
                </div>
              </div>
            </div>

            {/* Promise */}
            <div className="p-5 bg-gradient-to-r from-jkd-gold/10 to-transparent rounded-lg border-l-4 border-jkd-gold">
              <p className="text-gray-300 italic text-sm">
                {t('contact.promise')}
              </p>
              <p className="text-jkd-gold text-sm mt-2">{t('contact.promiseAuthor')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
